<template>
  <div
    class="relative w-full h-full bg-black overflow-hidden video-player-container"
    @click="handleContainerClick"
    @touchstart="handleContainerTouchStart"
  >
    <video
      :ref="`video-player-${id}`"
      class="video-js w-full h-full"
      playsinline
      webkit-playsinline
    ></video>

    <!-- Tap overlay - captures taps when controls are hidden -->
    <div
      v-if="showCustomControls && !controlsVisible"
      class="absolute inset-0 z-10"
      style="pointer-events: auto"
      @click="handleTapClick"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    ></div>

    <!-- Custom Controls Overlay -->
    <div
      v-if="showCustomControls && controlsVisible"
      class="absolute inset-0 flex flex-col z-20"
      :class="{ 'opacity-0 pointer-events-none': !controlsOpacity, 'opacity-100': controlsOpacity }"
      style="transition: opacity 0.25s ease-in-out"
      @click="handleControlsOverlayClick"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @mousemove="resetControlsIdleTimer"
      @mouseenter="resetControlsIdleTimer"
    >
      <!-- Top Navigation Buttons -->
      <div
        class="absolute top-0 left-0 right-0 flex items-center justify-between z-20 pointer-events-auto"
        style="
          padding-top: max(1rem, env(safe-area-inset-top));
          padding-left: max(calc((100vw - 100vh * 16 / 9) / 2 + 1.5rem), 1.5rem);
          padding-right: max(calc((100vw - 100vh * 16 / 9) / 2 + 1.5rem), 1.5rem);
        "
        @click.stop
        @touchstart.stop
        @touchmove.stop
        @touchend.stop
      >
        <!-- Back Button -->
        <button
          class="flex items-center justify-center transition-transform active:scale-95 focus:outline-none"
          style="width: clamp(40px, 5vw, 56px); height: clamp(40px, 5vw, 56px)"
          @click="handleHomeClick"
        >
          <img
            src="/assets/absolutely/player-back.svg"
            alt="Back"
            class="w-full h-full object-contain select-none pointer-events-none"
            draggable="false"
          />
        </button>
      </div>

      <!-- Central Playback Controls -->
      <div
        class="flex-1 flex items-center justify-center gap-6 z-10 pointer-events-auto"
        @click.stop
        @touchstart.stop
        @touchmove.stop
        @touchend.stop
      >
        <!-- Rewind 15s Button -->
        <button
          class="control-button bg-black/60 backdrop-blur-sm rounded-full flex flex-col items-center justify-center shadow-lg hover:bg-black/80 transition-colors relative"
          style="width: clamp(48px, 6vw, 64px); height: clamp(48px, 6vw, 64px)"
          @click="rewind15"
        >
          <img
            src="/assets/absolutely/player-prev-button.svg"
            alt="Rewind 15s"
            class="w-full h-full object-contain select-none pointer-events-none"
            draggable="false"
          />
        </button>

        <!-- Play/Pause Button -->
        <button
          class="control-button bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-black/80 transition-colors"
          style="width: clamp(72px, 9vw, 96px); height: clamp(72px, 9vw, 96px)"
          @click="togglePlayPause"
        >
          <img
            v-if="!isPlaying"
            src="/assets/absolutely/player-play-button.svg"
            alt="Play"
            class="w-full h-full object-contain select-none pointer-events-none"
            draggable="false"
          />
          <img
            v-else
            src="/assets/absolutely/player-pause-button.svg"
            alt="Pause"
            class="w-full h-full object-contain select-none pointer-events-none"
            draggable="false"
          />
        </button>

        <!-- Fast Forward 15s Button -->
        <button
          class="control-button bg-black/60 backdrop-blur-sm rounded-full flex flex-col items-center justify-center shadow-lg hover:bg-black/80 transition-colors relative"
          style="width: clamp(48px, 6vw, 64px); height: clamp(48px, 6vw, 64px)"
          @click="forward15"
        >
          <img
            src="/assets/absolutely/player-next-button.svg"
            alt="Forward 15s"
            class="w-full h-full object-contain select-none pointer-events-none"
            draggable="false"
          />
        </button>
      </div>

      <!-- Bottom Info and Progress Bar -->
      <div
        class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent z-10 pointer-events-auto"
        style="
          padding-top: clamp(0.75rem, 2vh, 1rem);
          padding-bottom: 1rem;
          padding-left: max(calc((100vw - 100vh * 16 / 9) / 2 + 1.5rem), 1.5rem);
          padding-right: max(calc((100vw - 100vh * 16 / 9) / 2 + 1.5rem), 1.5rem);
        "
        @click.stop
      >
        <div class="flex flex-col">
          <!-- Title -->
          <div class="flex flex-col">
            <h3 class="text-white" style="font-size: clamp(16px, 2vw, 20px); line-height: 100%">
              {{ title }}
            </h3>
          </div>

          <!-- Episode and Timer on same line -->
          <div class="flex justify-between items-center -mb-1">
            <p class="text-white opacity-90 font-[300]" style="font-size: clamp(12px, 1.5vw, 14px)">
              {{ chapterInfo }}
            </p>
            <span class="text-white font-[300]" style="font-size: clamp(12px, 1.5vw, 14px)"
              >{{ currentTimeFormatted }} / {{ durationFormatted }}</span
            >
          </div>

          <!-- Progress Bar -->
          <div
            class="relative w-full cursor-pointer progress-bar-wrapper"
            style="
              touch-action: pan-x;
              -webkit-tap-highlight-color: transparent;
              padding-top: 2px;
              padding-bottom: 6px;
            "
            @click.stop="seekTo"
            @touchstart.stop.prevent="handleProgressBarTouchStart"
            @touchmove.stop.prevent="handleProgressBarTouchMove"
            @touchend.stop.prevent="handleProgressBarTouchEnd"
            @mousedown.stop="handleProgressBarMouseDown"
            @mousemove.stop="handleProgressBarMouseMove"
            @mouseup.stop="handleProgressBarMouseUp"
            @mouseleave.stop="handleProgressBarMouseLeave"
          >
            <div
              class="relative w-full h-1 bg-white/30 rounded-full progress-bar-container"
              style="touch-action: pan-x; -webkit-tap-highlight-color: transparent"
            >
              <div
                class="absolute top-0 left-0 h-full bg-white rounded-full progress-bar-fill"
                :style="{ width: computedVisualProgressPercentage + '%' }"
              ></div>
              <div
                class="absolute top-1/2 transform -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg progress-bar-thumb"
                :style="{ left: computedVisualProgressPercentage + '%', marginLeft: '-6px' }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  loadVideoProgressFromStorage,
  saveVideoProgressToStorage,
  clearVideoProgressFromStorage,
} from '@/utils/absolutely-storage';
// The "!" tells webpack to skip the normal processing for this file
// This prevents Web Workers from breaking due to mangled variable names
import videojs from '!video.js';
import 'video.js/dist/video-js.css';
import 'videojs-contrib-quality-levels';
import '@mycujoo/videojs-hls-quality-selector';

// Use VideoPlayer's VHS buffer settings
videojs.Vhs.GOAL_BUFFER_LENGTH = 30;
videojs.Vhs.MAX_GOAL_BUFFER_LENGTH = 60 * 60;

export default {
  name: 'AbsolutelyVideoPlayer',
  props: {
    videoUrl: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    autoplay: {
      type: [Boolean, String],
      default: true,
    },
    controls: {
      type: Boolean,
      default: false,
    },
    fluid: {
      type: Boolean,
      default: false,
    },
    playerOptions: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: '',
    },
    chapterInfo: {
      type: String,
      default: '',
    },
    showCustomControls: {
      type: Boolean,
      default: true,
    },
    chapterId: {
      type: Number,
      default: null,
    },
    stepId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      player: null,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      controlsTimeout: null,
      controlsVisible: true,
      controlsOpacity: true,
      touchStartX: 0,
      touchStartY: 0,
      touchStartTime: 0,
      isDragging: false,
      isDraggingProgressBar: false,
      progressBarDragStartX: 0,
      progressInterval: null,
      lastSyncedProgress: 0,
      isInitialSeekDone: false,
      visualProgressPercentage: 0,
      targetProgressPercentage: 0,
      animationFrameId: null,
      debugId: `AVP-${Date.now()}`, // For debugging video player issues
    };
  },
  computed: {
    ...mapGetters('absolutely', [
      'getVideoProgress',
      'isStepCompleted',
      'loadVideoProgressFromStorage',
      'isVideoProgressCompleted',
    ]),
    // Override metadata to use videoUrl prop instead of requiring metadata object
    metadata() {
      return {
        url: this.videoUrl,
        thumbnail: '',
        title: 'Absolutely Onboarding',
      };
    },
    progressPercentage() {
      if (!this.duration) return 0;
      return (this.currentTime / this.duration) * 100;
    },
    computedVisualProgressPercentage() {
      return this.isDraggingProgressBar ? this.visualProgressPercentage : this.progressPercentage;
    },
    currentTimeFormatted() {
      return this.formatTime(this.currentTime);
    },
    durationFormatted() {
      return this.formatTime(this.duration);
    },
    // Override playerOptions for onboarding-specific settings
    computedPlayerOptions() {
      const videoUrl = this.videoUrl;

      if (!videoUrl) {
        console.error(`[${this.debugId}] WARNING: videoUrl is empty! Player will not work.`);
      }

      const isHlsStream =
        videoUrl.includes('.m3u8') ||
        videoUrl.includes('manifest/video.m3u8') ||
        videoUrl.includes('/hls/') ||
        videoUrl.match(/\.m3u8(\?|$)/i);
      const videoType = isHlsStream ? 'application/x-mpegURL' : 'video/mp4';

      console.log(`[${this.debugId}] Video type detection:`, {
        videoUrl,
        isHlsStream,
        videoType,
      });

      // Detect if we should use native HLS (iOS/iPad native apps)
      const isIOSNative =
        /iPad|iPhone|iPod/.test(navigator.userAgent) && !/Safari/.test(navigator.userAgent);
      const useNativeHls = isHlsStream && isIOSNative;

      console.log(`[${this.debugId}] HLS Configuration:`, {
        isHlsStream,
        isIOSNative,
        useNativeHls,
        userAgent: navigator.userAgent,
      });

      let autoplayValue = this.autoplay;
      let shouldMute = false;
      if (autoplayValue === true) {
        autoplayValue = 'play';
        shouldMute = false;
      } else if (autoplayValue === 'muted') {
        shouldMute = true;
      }

      const defaultOptions = {
        autoplay: autoplayValue,
        controls: false,
        fluid: this.fluid,
        muted: shouldMute,
        playsinline: true,
        preload: 'auto',
        techOrder: ['html5'],
        sources: [{ src: videoUrl, type: videoType }],
        html5: {
          vhs: {
            experimentalBufferBasedABR: true,
            overrideNative: !useNativeHls, // Use native HLS on iOS native
            enableLowInitialPlaylist: true,
            smoothQualityChange: true,
          },
          hls: {
            limitRenditionByPlayerDimensions: true,
            useDevicePixelRatio: true,
            withCredentials: false,
          },
          nativeVideoTracks: false,
          nativeAudioTracks: false,
          nativeTextTracks: false,
        },
      };

      return {
        ...defaultOptions,
        ...this.playerOptions,
        sources: this.playerOptions.sources || defaultOptions.sources,
        html5: {
          ...defaultOptions.html5,
          ...(this.playerOptions.html5 || {}),
          vhs: {
            ...defaultOptions.html5.vhs,
            ...(this.playerOptions.html5?.vhs || {}),
          },
          hls: {
            ...defaultOptions.html5.hls,
            ...(this.playerOptions.html5?.hls || {}),
          },
          nativeVideoTracks:
            this.playerOptions.html5?.nativeVideoTracks ?? defaultOptions.html5.nativeVideoTracks,
          nativeAudioTracks:
            this.playerOptions.html5?.nativeAudioTracks ?? defaultOptions.html5.nativeAudioTracks,
          nativeTextTracks:
            this.playerOptions.html5?.nativeTextTracks ?? defaultOptions.html5.nativeTextTracks,
        },
      };
    },
  },
  mounted() {
    console.log(`[${this.debugId}] AbsolutelyVideoPlayer mounted`);
    console.log(`[${this.debugId}] Props:`, {
      id: this.id,
      videoUrl: this.videoUrl,
      autoplay: this.autoplay,
      chapterId: this.chapterId,
      stepId: this.stepId,
      title: this.title,
      showCustomControls: this.showCustomControls,
    });

    const videoRefName = `video-player-${this.id}`;
    const videoElement = this.$refs[videoRefName];
    console.log(`[${this.debugId}] Video ref "${videoRefName}":`, videoElement);

    if (!videoElement) {
      console.error(`[${this.debugId}] ERROR: Video element ref not found!`);
      return;
    }

    console.log(
      `[${this.debugId}] Initializing Video.js with options:`,
      this.computedPlayerOptions,
    );

    try {
      this.player = videojs(videoElement, this.computedPlayerOptions, () => {
        console.log(`[${this.debugId}] Video.js player ready callback fired`);
        this.player.log('onPlayerReady', this);

        this.$nextTick(() => {
          try {
            const tech = this.player.tech();
            const videoEl = tech && tech.el ? tech.el() : null;
            console.log(`[${this.debugId}] Video element from tech:`, {
              hasTech: !!tech,
              hasTechEl: !!tech?.el,
              videoEl,
              tagName: videoEl?.tagName,
            });

            if (videoEl && videoEl.tagName === 'VIDEO') {
              videoEl.setAttribute('playsinline', 'true');
              videoEl.setAttribute('webkit-playsinline', 'true');
              videoEl.setAttribute('x5-playsinline', 'true');
              videoEl.setAttribute('x-webkit-airplay', 'deny');
              console.log(`[${this.debugId}] Set playsinline attributes on video element`);

              const preventFullscreen = e => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              };
              videoEl.addEventListener('webkitbeginfullscreen', preventFullscreen);
              videoEl.addEventListener('webkitendfullscreen', preventFullscreen);
            }
          } catch (error) {
            console.warn(`[${this.debugId}] Could not set playsinline attributes:`, error);
          }
        });

        if (this.autoplay && this.autoplay !== false) {
          console.log(`[${this.debugId}] Attempting autoplay...`);
          const playPromise = this.player.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log(`[${this.debugId}] Autoplay successful`);
              })
              .catch(error => {
                console.warn(`[${this.debugId}] Autoplay with sound prevented:`, error);
                if (this.autoplay !== 'muted') {
                  console.log(`[${this.debugId}] Falling back to muted autoplay`);
                  this.player.muted(true);
                  this.player.play().catch(err => {
                    console.warn(`[${this.debugId}] Muted autoplay also prevented:`, err);
                  });
                }
              });
          }
        }
      });

      console.log(`[${this.debugId}] Video.js instance created:`, !!this.player);

      // Log video.js version and available methods
      console.log(`[${this.debugId}] Video.js version:`, videojs.VERSION);
      console.log(
        `[${this.debugId}] Player methods:`,
        Object.keys(this.player)
          .filter(k => typeof this.player[k] === 'function')
          .slice(0, 20),
      );
    } catch (error) {
      console.error(`[${this.debugId}] ERROR initializing Video.js:`, error);
    }

    this.addVideoListeners();

    this.$nextTick(() => {
      const container = this.$el;
      if (container) {
        container.addEventListener('click', this.handleContainerClick, true);
        container.addEventListener('touchstart', this.handleContainerTouchStart, {
          capture: true,
          passive: false,
        });
        console.log(`[${this.debugId}] Container event listeners attached`);
      }
    });

    if (this.chapterId && this.stepId) {
      this.player.ready(() => {
        this.$nextTick(() => {
          setTimeout(() => {
            console.log(`[${this.debugId}] Starting progress tracking`);
            this.startProgressTracking();
          }, 500);
        });
      });
    }

    console.log(`[${this.debugId}] Mounted complete, player state:`, {
      hasPlayer: !!this.player,
      isReady: this.player ? this.player.isReady_ : 'N/A',
    });
  },
  beforeUnmount() {
    this.stopProgressTracking();
    this.clearControlsTimeout();

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    const container = this.$el;
    if (container) {
      container.removeEventListener('click', this.handleContainerClick, true);
      container.removeEventListener('touchstart', this.handleContainerTouchStart, {
        capture: true,
        passive: false,
      });
    }

    if (this.player) {
      this.player.dispose();
    }
  },
  methods: {
    ...mapActions('absolutely', ['syncProgress']),
    addVideoListeners() {
      console.log(`[${this.debugId}] Adding video listeners`);
      this.player.on('ended', () => {
        console.log(`[${this.debugId}] EVENT: video ended`);
        this.handleVideoEnd();
      });
      this.player.on('play', () => {
        console.log(`[${this.debugId}] EVENT: video play`);
        this.isPlaying = true;
        this.startProgressTracking();
        if (this.controlsVisible) {
          this.resetControlsIdleTimer();
        }
      });
      this.player.on('pause', () => {
        console.log(`[${this.debugId}] EVENT: video pause`);
        this.isPlaying = false;
        this.clearControlsTimeout();
        this.handlePauseProgress();
      });
      this.player.on('timeupdate', () => {
        this.currentTime = this.player.currentTime();
        // Sync visual progress when not dragging (smooth catch-up)
        if (!this.isDraggingProgressBar) {
          this.visualProgressPercentage = this.progressPercentage;
        }
      });
      // Track seeking events to update progress
      this.player.on('seeking', () => {
        console.log(
          `[${this.debugId}] EVENT: video seeking to ${this.player.currentTime().toFixed(2)}s`,
        );
        // When user seeks, update progress immediately
        if (this.chapterId && this.stepId) {
          const seekTime = this.player.currentTime();
          if (seekTime > 0) {
            this.handleProgressUpdate(seekTime);
          }
        }
      });
      this.player.on('loadedmetadata', () => {
        this.duration = this.player.duration();
        console.log(`[${this.debugId}] EVENT: loadedmetadata, duration: ${this.duration}s`);
        // Initialize visual progress percentage
        this.visualProgressPercentage = this.progressPercentage;
        // After metadata loads, perform initial seek if needed
        if (!this.isInitialSeekDone && this.chapterId && this.stepId && this.duration > 0) {
          // Use setTimeout to ensure duration is fully set
          setTimeout(() => {
            this.loadSavedProgress();
            // Sync visual progress after initial seek
            this.visualProgressPercentage = this.progressPercentage;
          }, 100);
        }
      });
      this.player.on('durationchange', () => {
        this.duration = this.player.duration();
        console.log(`[${this.debugId}] EVENT: durationchange, new duration: ${this.duration}s`);
      });
      this.player.on('error', () => {
        const error = this.player.error();
        console.error(`[${this.debugId}] EVENT: video error:`, error);
      });
      this.player.on('loadstart', () => {
        console.log(`[${this.debugId}] EVENT: loadstart`);
      });
      this.player.on('canplay', () => {
        console.log(`[${this.debugId}] EVENT: canplay`);
      });
      this.player.on('waiting', () => {
        console.log(`[${this.debugId}] EVENT: waiting (buffering)`);
      });
      this.player.on('playing', () => {
        console.log(`[${this.debugId}] EVENT: playing`);
      });
    },
    handleVideoEnd() {
      // Stop progress tracking when video ends (prevent any more progress updates)
      this.stopProgressTracking();

      // Get final video progress (should be duration or currentTime)
      const finalProgress = this.player?.currentTime() ?? this.duration ?? 0;

      // Set localStorage to 0 when video completes (instead of clearing)
      // This allows the check to treat 0 as "no progress"
      if (this.chapterId && this.stepId) {
        this.saveProgressToLocalStorage(0);
      }

      // Save final progress to backend (but don't save to localStorage)
      if (this.chapterId && this.stepId && finalProgress > 0) {
        // Save final progress to backend only (not localStorage)
        this.saveProgressToBackend(finalProgress);
      }

      // Show controls when video ends - fade in
      if (this.showCustomControls) {
        this.clearControlsTimeout();
        this.fadeInControls();
      }

      // Emit event with final progress - let parent component handle navigation
      this.$emit('video-ended', finalProgress);
    },
    // Load saved progress from backend and seek to that position
    async loadSavedProgress() {
      if (!this.chapterId || !this.stepId || !this.player) {
        return;
      }

      try {
        // Always check localStorage first (current session progress)
        // This allows users to retry and resume from where they paused, even if step is completed
        const localStorageProgress = this.loadVideoProgressFromStorageLocal();

        // If localStorage has valid progress (> 0), use it
        // If localStorage is 0 or null, start from beginning (don't check backend to avoid API call)
        if (localStorageProgress !== null && localStorageProgress > 0 && this.duration > 0) {
          // Clamp progress to video duration
          const seekTime = Math.min(localStorageProgress, this.duration - 1);
          this.player.currentTime(seekTime);
          this.isInitialSeekDone = true;
        } else {
          // No localStorage progress (0 or null) - start from beginning
          // Don't check backend to avoid unnecessary API call
          this.isInitialSeekDone = true;
        }
      } catch (error) {
        console.warn('[AbsolutelyVideoPlayer] Error loading saved progress:', error);
      }
    },
    // Save progress to backend
    async saveProgressToBackend(progressSeconds) {
      if (!this.chapterId || !this.stepId) {
        return;
      }

      // Get latest saved progress from backend (source of truth)
      const savedProgress = this.getVideoProgress(this.chapterId, this.stepId);

      // Don't update if saved progress is already greater than current progress
      if (savedProgress !== null && savedProgress > progressSeconds) {
        return;
      }

      // Avoid duplicate syncs if progress hasn't changed significantly
      if (Math.abs(progressSeconds - this.lastSyncedProgress) < 1) {
        return;
      }

      try {
        const progressString = String(progressSeconds.toFixed(1));
        await this.syncProgress({
          chapterId: this.chapterId,
          stepId: this.stepId,
          isCompleted: false, // Don't mark as completed, just update progress
          videoProgress: progressString,
        });
        this.lastSyncedProgress = progressSeconds;
      } catch (error) {
        console.warn('[AbsolutelyVideoPlayer] Error saving progress to backend:', error);
      }
    },
    // Save progress to local storage (for current session)
    saveProgressToLocalStorage(progressSeconds) {
      if (!this.chapterId || !this.stepId) {
        return;
      }

      // Always save to localStorage (current session tracking)
      // localStorage is independent from backend and tracks current session progress
      // This allows users to pause and resume within the same session
      saveVideoProgressToStorage(this.chapterId, this.stepId, progressSeconds);
    },
    // Load progress from local storage (for current session) - uses utility function
    loadVideoProgressFromStorageLocal() {
      return loadVideoProgressFromStorage(this.chapterId, this.stepId);
    },
    // Clear progress from local storage
    clearVideoProgressFromStorage() {
      if (!this.chapterId || !this.stepId) {
        return;
      }
      // Use utility function to clear from localStorage
      clearVideoProgressFromStorage(this.chapterId, this.stepId);
    },
    // Start progress tracking interval (every 5 seconds)
    startProgressTracking() {
      // Clear any existing interval
      this.stopProgressTracking();

      if (!this.chapterId || !this.stepId || !this.player) {
        return;
      }

      // Track progress every 5 seconds
      this.progressInterval = setInterval(() => {
        if (this.player && this.isPlaying) {
          const currentTime = this.player.currentTime();
          if (currentTime > 0 && this.duration > 0) {
            this.handleProgressUpdate(currentTime);
          }
        }
      }, 5000); // 5 seconds
    },
    // Stop progress tracking interval
    stopProgressTracking() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    },
    // Handle progress update (called every 5 seconds)
    async handleProgressUpdate(currentTime) {
      if (!this.chapterId || !this.stepId) {
        return;
      }

      // Don't update if video has ended (player might still be emitting events)
      if (!this.player || this.player.ended()) {
        return;
      }

      // Save to both backend and local storage
      await this.saveProgressToBackend(currentTime);
      this.saveProgressToLocalStorage(currentTime);
    },
    // Handle progress update on pause
    async handlePauseProgress() {
      if (!this.chapterId || !this.stepId || !this.player) {
        return;
      }

      // Don't update if video has ended
      if (this.player.ended()) {
        return;
      }

      const currentTime = this.player.currentTime();
      if (currentTime > 0 && this.duration > 0) {
        // Save to localStorage only (no backend call on pause)
        this.saveProgressToLocalStorage(currentTime);
      }
    },
    togglePlayPause() {
      if (this.player) {
        if (this.isPlaying) {
          this.player.pause();
        } else {
          this.player.play();
        }
        // Reset idle timer when interacting with play/pause
        if (this.controlsVisible) {
          this.resetControlsIdleTimer();
        }
      }
    },
    rewind15() {
      if (this.player) {
        const newTime = Math.max(0, this.player.currentTime() - 15);
        this.player.currentTime(newTime);
        this.resetControlsIdleTimer();
      }
    },
    forward15() {
      if (this.player) {
        const newTime = Math.min(this.duration, this.player.currentTime() + 15);
        this.player.currentTime(newTime);
        this.resetControlsIdleTimer();
      }
    },
    seekTo(event) {
      if (this.player && this.duration) {
        // Find the actual progress bar element (might be wrapper or container)
        const progressBarElement =
          event.currentTarget.querySelector('.progress-bar-container') || event.currentTarget;
        const rect = progressBarElement.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, clickX / rect.width));
        const newTime = percentage * this.duration;

        // Set target position for smooth catch-up animation
        this.targetProgressPercentage = percentage * 100;

        // Start smooth catch-up animation if not already running
        if (!this.animationFrameId) {
          this.animateProgressCatchUp();
        }

        // Update video position
        this.player.currentTime(newTime);
      }
    },
    // Smooth catch-up animation during drag
    animateProgressCatchUp() {
      const animate = () => {
        if (!this.isDraggingProgressBar) {
          // Stop animation when drag ends
          this.animationFrameId = null;
          return;
        }

        // Smooth catch-up: move visual position towards target
        const diff = this.targetProgressPercentage - this.visualProgressPercentage;
        const speed = 0.3; // Catch-up speed (0-1, higher = faster)
        this.visualProgressPercentage += diff * speed;

        // Continue animation if still dragging
        if (Math.abs(diff) > 0.1) {
          this.animationFrameId = requestAnimationFrame(animate);
        } else {
          // Close enough, snap to target
          this.visualProgressPercentage = this.targetProgressPercentage;
          this.animationFrameId = null;
        }
      };

      this.animationFrameId = requestAnimationFrame(animate);
    },
    handleProgressBarTouchStart(event) {
      // Mark that we're starting to drag the progress bar
      this.isDraggingProgressBar = true;
      this.progressBarDragStartX = event.touches[0].clientX;
      // Initialize visual position to current progress
      this.visualProgressPercentage = this.progressPercentage;
      this.targetProgressPercentage = this.progressPercentage;
      // CRITICAL for native: preventDefault must be called synchronously
      // and stopPropagation to prevent parent handlers from interfering
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation(); // Prevent other handlers on same element
      // Ensure controls are visible and stop idle timer while dragging
      if (this.showCustomControls) {
        this.clearControlsTimeout(); // Stop any pending fade out
        if (!this.controlsVisible) {
          this.fadeInControls();
        } else if (!this.controlsOpacity) {
          this.controlsOpacity = true;
        }
      }
      // Find the actual progress bar element (might be wrapper or container)
      const progressBarElement =
        event.currentTarget.querySelector('.progress-bar-container') || event.currentTarget;
      this.seekToTouch(event, progressBarElement);
    },
    handleProgressBarTouchMove(event) {
      if (this.isDraggingProgressBar) {
        // CRITICAL for native: preventDefault to stop scrolling/zooming while dragging
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        // Keep controls visible and prevent idle timer during drag
        if (this.showCustomControls) {
          this.clearControlsTimeout(); // Prevent fade out while dragging
        }
        // Find the actual progress bar element
        const progressBarElement =
          event.currentTarget.querySelector('.progress-bar-container') || event.currentTarget;
        this.seekToTouch(event, progressBarElement);
      }
    },
    handleProgressBarTouchEnd(event) {
      if (this.isDraggingProgressBar) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.isDraggingProgressBar = false;
        this.progressBarDragStartX = 0;
        // Stop animation
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
        // Sync visual progress to actual progress after drag ends
        this.visualProgressPercentage = this.progressPercentage;
        this.targetProgressPercentage = this.progressPercentage;
        // Restart idle timer after drag ends (if video is playing)
        if (
          this.showCustomControls &&
          this.isPlaying &&
          this.player &&
          !this.player.paused() &&
          !this.player.ended()
        ) {
          this.resetControlsIdleTimer();
        }
      }
    },
    seekToTouch(event, progressBarElement) {
      if (this.player && this.duration && event.touches && event.touches.length > 0) {
        // Use the provided element or fallback to currentTarget
        const progressBar = progressBarElement || event.currentTarget;
        const rect = progressBar.getBoundingClientRect();
        const touchX = event.touches[0].clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, touchX / rect.width));
        const newTime = percentage * this.duration;

        // Set target position for smooth catch-up animation
        this.targetProgressPercentage = percentage * 100;

        // Start smooth catch-up animation if not already running
        if (!this.animationFrameId) {
          this.animateProgressCatchUp();
        }

        // Update video position
        this.player.currentTime(newTime);
      }
    },
    handleProgressBarMouseDown(event) {
      this.isDraggingProgressBar = true;
      this.progressBarDragStartX = event.clientX;
      // Initialize visual position to current progress
      this.visualProgressPercentage = this.progressPercentage;
      this.targetProgressPercentage = this.progressPercentage;
      event.stopPropagation();
      // Ensure controls are visible and stop idle timer while dragging
      if (this.showCustomControls) {
        this.clearControlsTimeout(); // Stop any pending fade out
        if (!this.controlsVisible) {
          this.fadeInControls();
        } else if (!this.controlsOpacity) {
          this.controlsOpacity = true;
        }
      }
      this.seekTo(event);
    },
    handleProgressBarMouseMove(event) {
      if (this.isDraggingProgressBar) {
        event.preventDefault();
        event.stopPropagation();
        // Keep controls visible and prevent idle timer during drag
        if (this.showCustomControls) {
          this.clearControlsTimeout(); // Prevent fade out while dragging
        }
        this.seekTo(event);
      }
    },
    handleProgressBarMouseUp(event) {
      if (this.isDraggingProgressBar) {
        event.stopPropagation();
        this.isDraggingProgressBar = false;
        this.progressBarDragStartX = 0;
        // Stop animation
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
        // Sync visual progress to actual progress after drag ends
        this.visualProgressPercentage = this.progressPercentage;
        this.targetProgressPercentage = this.progressPercentage;
        // Restart idle timer after drag ends (if video is playing)
        if (
          this.showCustomControls &&
          this.isPlaying &&
          this.player &&
          !this.player.paused() &&
          !this.player.ended()
        ) {
          this.resetControlsIdleTimer();
        }
      }
    },
    handleProgressBarMouseLeave(_event) {
      if (this.isDraggingProgressBar) {
        this.isDraggingProgressBar = false;
        this.progressBarDragStartX = 0;
        // Stop animation
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
        // Sync visual progress to actual progress when mouse leaves
        this.visualProgressPercentage = this.progressPercentage;
        this.targetProgressPercentage = this.progressPercentage;
      }
    },
    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '0:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },
    fadeInControls() {
      this.controlsVisible = true;
      this.controlsOpacity = false;
      this.$nextTick(() => {
        // Small delay to ensure DOM is rendered before starting transition
        setTimeout(() => {
          this.controlsOpacity = true;
        }, 10);
      });
    },
    fadeOutControls() {
      this.controlsOpacity = false;
      setTimeout(() => {
        if (!this.controlsOpacity) {
          this.controlsVisible = false;
        }
      }, 250);
    },
    toggleControls() {
      const newValue = !this.controlsVisible;
      this.clearControlsTimeout();

      if (newValue) {
        this.fadeInControls();
      } else {
        this.fadeOutControls();
      }

      // Auto-hide only when controls are visible AND video is playing
      if (
        newValue &&
        this.isPlaying &&
        this.player &&
        !this.player.paused() &&
        !this.player.ended()
      ) {
        this.controlsTimeout = setTimeout(() => {
          if (this.isPlaying && this.player && !this.player.paused() && !this.player.ended()) {
            this.fadeOutControls();
          }
        }, 3000);
      }
    },
    handleContainerClick(event) {
      // In Chrome/desktop, click events work better than touch events
      // Check if click is on interactive elements (buttons, progress bar, etc.)
      const target = event.target;
      const isClickOnInteractiveElement =
        target?.tagName === 'BUTTON' ||
        target?.closest('button') ||
        target?.closest('.control-button') ||
        target?.closest('.progress-bar-container') ||
        target?.closest('svg') || // SVG icons in buttons
        (target?.classList?.contains('pointer-events-auto') && target?.tagName !== 'DIV'); // But not overlay divs

      if (!isClickOnInteractiveElement && this.showCustomControls) {
        this.toggleControls();
      }
    },
    handleContainerTouchStart(event) {
      this.handleTouchStart(event);
    },
    handleTouchStart(event) {
      // Track touch start position and time to distinguish taps from drags
      if (event.touches && event.touches.length > 0) {
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
        this.touchStartTime = Date.now();
        this.isDragging = false;
      }
    },
    handleTouchMove(event) {
      // Don't prevent drag if user is dragging the progress bar
      if (this.isDraggingProgressBar) {
        return;
      }

      // Reset idle timer when user moves finger on controls
      if (this.controlsVisible) {
        this.resetControlsIdleTimer();
      }

      // Detect if user is dragging (moving finger)
      if (event.touches && event.touches.length > 0 && this.touchStartX !== 0) {
        const deltaX = Math.abs(event.touches[0].clientX - this.touchStartX);
        const deltaY = Math.abs(event.touches[0].clientY - this.touchStartY);
        const moveDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // If moved more than 10px, consider it a drag
        if (moveDistance > 10) {
          this.isDragging = true;
          // Prevent default to stop scrolling/navigation
          event.preventDefault();
        }
      }
    },
    handleTouchEnd(event) {
      // Don't trigger tap action if user was dragging progress bar
      if (this.isDraggingProgressBar) {
        this.isDraggingProgressBar = false;
        this.progressBarDragStartX = 0;
        return;
      }

      // If touchStartX is 0, handleTouchStart wasn't called - treat as a tap anyway
      // This can happen if touch events are intercepted or handled elsewhere
      if (this.touchStartX === 0 && event.changedTouches && event.changedTouches.length > 0) {
        // Prevent click event from firing after this touch event
        event.preventDefault();
        // Toggle controls directly
        if (this.showCustomControls) {
          this.toggleControls();
        }
        // Reset touch tracking
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchStartTime = 0;
        this.isDragging = false;
        return;
      }

      // Only trigger tap action if it wasn't a drag
      if (!this.isDragging && this.touchStartX !== 0) {
        const touchDuration = Date.now() - this.touchStartTime;
        // Only treat as tap if touch duration is less than 300ms
        if (touchDuration < 300) {
          // Prevent click event from firing after this touch event
          event.preventDefault();
          // Toggle controls regardless of playing state
          if (this.showCustomControls) {
            this.toggleControls();
          }
        }
      }

      // Reset touch tracking
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchStartTime = 0;
      this.isDragging = false;
    },
    handleTapClick(_event) {
      if (!this.isDragging && this.showCustomControls) {
        this.toggleControls();
      }
    },
    handleControlsOverlayClick(_event) {
      if (!this.isDragging && this.showCustomControls) {
        this.toggleControls();
      }
    },
    resetControlsIdleTimer() {
      // Don't start idle timer if user is dragging the progress bar
      if (this.isDraggingProgressBar) {
        return;
      }
      if (
        !this.controlsVisible ||
        !this.isPlaying ||
        !this.player ||
        this.player.paused() ||
        this.player.ended()
      ) {
        return;
      }

      if (!this.controlsOpacity) {
        this.controlsOpacity = true;
      }
      this.clearControlsTimeout();

      this.controlsTimeout = setTimeout(() => {
        // Don't fade out if user is dragging the progress bar
        if (
          !this.isDraggingProgressBar &&
          this.isPlaying &&
          this.player &&
          !this.player.paused() &&
          !this.player.ended()
        ) {
          this.fadeOutControls();
        }
      }, 3000);
    },
    clearControlsTimeout() {
      if (this.controlsTimeout) {
        clearTimeout(this.controlsTimeout);
        this.controlsTimeout = null;
      }
    },
    handleHomeClick() {
      // Go back to previous page (chapter home page)
      if (this.$router) {
        this.$router.back();
      }
      this.$emit('home');
      this.resetControlsIdleTimer();
    },
    async scrollToTop() {
      const ionPage = this.$el?.closest('ion-page') || this.$el;
      const ionContent = ionPage?.querySelector('ion-content');
      if (ionContent) {
        try {
          const scrollElement = await ionContent.getScrollElement();
          scrollElement.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        } catch (error) {
          console.error('[AbsolutelyVideoPlayer] Error scrolling to top:', error);
        }
      }
    },
  },
};
</script>

<style scoped>
.video-js {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.video-js .vjs-tech {
  border-radius: 0px !important;
  object-fit: contain !important;
}

.video-js .vjs-control-bar {
  display: none !important;
}

.video-js .vjs-big-play-button {
  display: none !important;
}

/* Prevent native video controls from appearing */
.video-js video::-webkit-media-controls {
  display: none !important;
}

.video-js video::-webkit-media-controls-enclosure {
  display: none !important;
}

.video-js video::-webkit-media-controls-panel {
  display: none !important;
}

.control-button {
  transition:
    transform 0.2s,
    opacity 0.2s;
  position: relative;
}

.control-button:active {
  transform: scale(0.9);
  opacity: 0.8;
}

/* Prevent dragging and text selection on video player */
.video-player-container {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation; /* Optimize touch interactions, prevent double-tap zoom */
}

.video-player-container .video-js {
  touch-action: none; /* Prevent all touch actions on video.js container */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.video-player-container .video-js video {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  pointer-events: auto; /* Allow video to play, but prevent dragging */
}

/* Progress bar wrapper - provides larger touch target */
.progress-bar-wrapper {
  touch-action: pan-x !important; /* Allow horizontal dragging on progress bar - important for native */
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  /* Increase touch target area */
  min-height: 44px; /* Minimum touch target size for accessibility */
  display: flex;
  align-items: center;
  /* Ensure wrapper can capture touch events on native */
  position: relative;
  z-index: 1;
  /* Prevent touch events from being blocked */
  pointer-events: auto !important;
  /* Native-specific optimizations */
  -webkit-touch-callout: none;
  -webkit-user-drag: none;
}

/* Allow dragging on progress bar */
.progress-bar-container {
  touch-action: pan-x; /* Allow horizontal dragging on progress bar */
  cursor: grab;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  width: 100%;
  position: relative;
}

.progress-bar-container:active {
  cursor: grabbing;
}

/* Progress bar thumb - no CSS transitions, animation handled by JS */
.progress-bar-thumb {
  cursor: grab;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  touch-action: pan-x;
  pointer-events: none; /* Let wrapper handle touch events */
  /* Smooth animation handled by requestAnimationFrame */
}

.progress-bar-wrapper:active .progress-bar-thumb {
  cursor: grabbing;
}
</style>
